import { useWelcomeCouponOfferQuery } from "@/queries/welcome-coupon-offer-query";
import type { WelcomeCouponOffer } from "@/libs/welcome-coupon/getWelcomeOffer";

/**
 * 신규 가입 쿠폰이 **지금 발급되는가 + 무슨 조건인가**. 발급되지 않으면 `null`.
 *
 * **혜택을 약속하는 모든 표면은 이 훅을 쓸 것.** 반환이 `null` 이면 아무것도 노출하지 않고,
 * `null` 이 아니면 `percent`·`validForHours` 를 그대로 고지에 쓴다.
 *
 * ### 왜 상수가 아니라 서버 조회인가 (2026-09-10)
 * 종전에는 `WELCOME_COUPON` 상수(할인율·유효기간·**발급 마감 시각**)를 프론트가 미러하고,
 * 여기서 `플래그 AND 마감 전` 을 계산했다. 문제가 둘이었다.
 *
 * 1. **정본과 갈리면 잘못된 고지가 나간다.** mgmt 캠페인 정의가 정본인데 값이 두 벌이었다.
 *    2026-08-14 마감 직후 실제로 "가입시켜 놓고 쿠폰을 안 주는" 상태가 됐다.
 * 2. **캠페인마다 배포가 필요했다.** 마감 시각이 코드에 있으니 새 캠페인을 열 때마다
 *    프론트를 다시 배포해야 했다.
 *
 * 마감을 프론트에 심은 이유 자체는 정당했다 — 캠페인 플래그(`welcomeCouponEnabled`)는
 * **이미 발급된 쿠폰의 사용까지** 끄기 때문에(mgmt `requireUsable`) 발급이 끝나는 시점에
 * 내릴 수 없고, 그래서 플래그만으로는 광고를 내릴 수 없었다. 지금은 mgmt 가 그 판정을
 * `available` 하나로 흡수해 내려주므로 **프론트가 시각을 다루지 않는다.**
 *
 * ### fail-closed
 * 로딩 중·조회 실패·오퍼 없음은 전부 `null` 이다. 잘못 광고하는 쪽이 노출 누락보다 나쁘다.
 * 킬 스위치 판정도 서버가 하므로 여기서 플래그를 따로 읽지 않는다(읽으면 판정이 두 벌이 된다).
 *
 * ### 반대로 쿠폰 **사용** 표면은 이 훅을 쓰면 안 된다
 * 카트·체크아웃의 쿠폰 적용 UI 는 마감 후에도 보유자가 유효기간까지 써야 한다.
 * 그쪽 게이트는 `couponEnabled` 다.
 */
export function useWelcomeCouponIssuing(): WelcomeCouponOffer | null {
  const { data } = useWelcomeCouponOfferQuery();
  // `data?.available === true` 로 좁게 판정 — undefined(로딩)·false 는 모두 숨김.
  return data?.available === true ? data : null;
}

/**
 * `validForHours` 를 "N일" 표기로. **1일 미만이면 `null`** 이다.
 *
 * 내림하면 24시간 미만 캠페인에서 `0` 이 되어 "0일 안에 사용" 이 화면에 뜨고, 올림하면
 * 6시간 남은 쿠폰을 "1일" 이라고 과장한다. 둘 다 고지로 쓸 수 없어 **그 구간에는 유효기간을
 * 표기하지 않는다**(호출처가 `null` 이면 배지를 렌더하지 않는다). 실제 캠페인은 48·72·168
 * 시간이라 이 구간에 걸리지 않지만, 관리자가 짧은 캠페인을 만들 수 있다.
 *
 * 새 문구 키를 만들지 않은 이유: "N시간" 표기를 추가하면 7 로케일 번역이 필요하고, 정작
 * 그 구간이 운영에서 쓰이지 않는다.
 */
export function toValidDays(validForHours: number): number | null {
  const days = Math.floor(validForHours / 24);
  return days >= 1 ? days : null;
}
